import { useState } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Stack, Tab, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CloseIcon } from "../../../../../../../../../assets/icons";
import {
	BaseNodeDTO,
	BaseNodesListDTO,
	SearchKnownSitesPayload,
} from "../../../../../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../../../../../datasources/networking/apiRoutes";
import { Option } from "../../../../../../../../../datasources/responses/common";
import { useWriteResource } from "../../../../../../../../../hooks/useResource";
import { NetworkNodeTab } from "./components/NetworkNode";
import { StandardCodeTab } from "./components/StandardCodeTab";

type NodeDialogProps = {
	open: boolean;
	handleClose: VoidFunction;
	nodeTypes: Option[];
	countries: Option[];
	onSelectNode: (node: BaseNodeDTO) => void;
};

export type BaseNodeSearchTabProps = {
	onSearch: (payload: SearchKnownSitesPayload) => Promise<void>;
	onResetResults: VoidFunction;
	loading: boolean;
	nodes: BaseNodeDTO[];
	onSelectNode: (node: BaseNodeDTO) => void;
};

export type NetworkNodeTabProps = BaseNodeSearchTabProps & {
	nodeTypes: Option[];
	countries: Option[];
};

export const NodeDialog = ({ open, handleClose, nodeTypes, countries, onSelectNode }: NodeDialogProps) => {
	const { t } = useTranslation("dataCollection");

	const [tabValue, setTabValue] = useState("network");
	const [nodes, setNodes] = useState<BaseNodeDTO[]>([]);

	const { post: searchNodes, isLoading: loadingSearchNodes } = useWriteResource<
		SearchKnownSitesPayload,
		BaseNodesListDTO
	>({
		route: ApiRoutes.ConfigurationSearchNodes,
	});

	const resetResults = () => {
		setNodes([]);
	};

	const handleChangeTab = (_event: React.SyntheticEvent, newValue: string) => {
		setTabValue(newValue);
		resetResults();
	};

	const handleSearchNodes = async (payload: SearchKnownSitesPayload): Promise<void> => {
		try {
			const response = await searchNodes(payload);

			setNodes(response.items ?? []);
		} catch {
			setNodes([]);
		}
	};

	const handleCloseDialog = () => {
		setTabValue("network");
		setNodes([]);
		handleClose();
	};

	return (
		<Dialog onClose={handleCloseDialog} aria-labelledby="node-dialog-title" open={open} maxWidth="xl" fullWidth>
			<Stack direction="row" justifyContent="space-between" alignItems="start" sx={{ p: 2 }}>
				<DialogTitle component="h5" id="node-dialog-title" sx={{ p: 0 }}>
					{t("detail/search for system node")}

					<Typography variant="subtitle2" fontWeight={400} color="textSecondary">
						{t("detail/select the specific node")}
					</Typography>
				</DialogTitle>

				<IconButton aria-label="close" onClick={handleCloseDialog}>
					<CloseIcon />
				</IconButton>
			</Stack>

			<Divider sx={{ mx: 2 }} />

			<DialogContent sx={{ px: 2, py: 0 }}>
				<TabContext value={tabValue}>
					<Box
						sx={{
							borderBottom: 1,
							borderColor: "divider",
						}}
					>
						<TabList onChange={handleChangeTab} aria-label="search system node tabs">
							<Tab label={t("detail/network nodes")} value="network" />

							<Tab label={t("detail/un locode")} value="un-locode" />

							<Tab label={t("detail/iata")} value="iata" />

							<Tab label={t("detail/icao")} value="icao" />

							<Tab label={t("detail/wpi")} value="wpi" />
						</TabList>
					</Box>

					<TabPanel sx={{ py: 2, px: 0 }} value="network">
						<NetworkNodeTab
							onSearch={handleSearchNodes}
							onResetResults={resetResults}
							loading={loadingSearchNodes}
							nodeTypes={nodeTypes}
							countries={countries}
							nodes={nodes ?? []}
							onSelectNode={onSelectNode}
						/>
					</TabPanel>

					<TabPanel sx={{ py: 2, px: 0 }} value="un-locode">
						<StandardCodeTab
							searchCode="unlocode"
							onSearch={handleSearchNodes}
							onResetResults={resetResults}
							loading={loadingSearchNodes}
							nodes={nodes ?? []}
							onSelectNode={onSelectNode}
						/>
					</TabPanel>

					<TabPanel sx={{ py: 2, px: 0 }} value="iata">
						<StandardCodeTab
							searchCode="iata"
							onSearch={handleSearchNodes}
							onResetResults={resetResults}
							loading={loadingSearchNodes}
							nodes={nodes ?? []}
							onSelectNode={onSelectNode}
						/>
					</TabPanel>

					<TabPanel sx={{ py: 2, px: 0 }} value="icao">
						<StandardCodeTab
							searchCode="icao"
							onSearch={handleSearchNodes}
							onResetResults={resetResults}
							loading={loadingSearchNodes}
							nodes={nodes ?? []}
							onSelectNode={onSelectNode}
						/>
					</TabPanel>

					<TabPanel sx={{ py: 2, px: 0 }} value="wpi">
						<StandardCodeTab
							searchCode="wpi"
							onSearch={handleSearchNodes}
							onResetResults={resetResults}
							loading={loadingSearchNodes}
							nodes={nodes ?? []}
							onSelectNode={onSelectNode}
						/>
					</TabPanel>
				</TabContext>
			</DialogContent>
		</Dialog>
	);
};
